import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ItemWithMetaData } from "../api/Items";
import { SCRAPE_STATUS, SCRAPE_TYPES } from "../api/Profiles";
import { ReportSummaryItemApi } from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { usePostScrapeDone } from "../api/WorkflowApi";
import Button from "../shared-components/Button";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { classNames } from "../utils";
import { ManualProfileRow } from "./ManualProfileRow";
import { useGetComputedBlueprintFromWorkflow } from "../api/BlueprintsApi";
import {
  ManualProfileRowDataBadge,
  ManualProfileRowType,
  MANUAL_PROFILE_ACTION,
} from "./ManualProfileRowData";
import {
  useReportDetail,
  useReportTimer,
} from "../reports/ReportDetailProvider";

type Props = {
  isReadOnly?: boolean;
  report?: ReportSummaryItemApi;
  handleExit: () => void;
  isAmendment: boolean;
};

interface ProfilesState {
  profilesToScrape: ItemWithMetaData[];
  skippedProfiles: ItemWithMetaData[];
  systemProfiles: ItemWithMetaData[];
}

const defaultState = {
  profilesToScrape: [],
  skippedProfiles: [],
  systemProfiles: [],
};

function buildProfilesState(
  profilesResponse?: ItemWithMetaData[]
): ProfilesState {
  if (!profilesResponse) return defaultState;

  const profilesToScrape = profilesResponse?.filter((profile) => {
    const scrapeType = profile.item.content.scrape_type;
    return (
      scrapeType === SCRAPE_TYPES.MANUAL &&
      profile.item.content.scrape_status !== "SKIPPED"
    );
  });

  const skippedProfiles = profilesResponse?.filter((profile) => {
    return profile.item.content.scrape_status === "SKIPPED";
  });

  const systemProfiles = profilesResponse?.filter((profile) => {
    const scrapeType = profile.item.content.scrape_type;
    return (
      (!scrapeType || scrapeType === SCRAPE_TYPES.AUTO) &&
      profile.item.content.scrape_status !== "SKIPPED"
    );
  });

  return { profilesToScrape, skippedProfiles, systemProfiles };
}

function SkippedProfilesSummary({
  profiles,
}: {
  profiles: ItemWithMetaData[];
}) {
  const countDoneProfiles = profiles.filter(
    (profile) => profile.item.content.scrape_status === SCRAPE_STATUS.DONE
  ).length;

  const countInProgressProfiles = profiles.filter((profile) => {
    const status = profile.item.content.scrape_status;
    return !status || status === SCRAPE_STATUS.IN_PROGRESS;
  }).length;

  return (
    <div className="flex gap-4 text-sm">
      <ManualProfileRowDataBadge status={SCRAPE_STATUS.IN_PROGRESS}>
        <span className="mr-2">{countInProgressProfiles}</span>
      </ManualProfileRowDataBadge>
      <ManualProfileRowDataBadge status={SCRAPE_STATUS.DONE}>
        <span className="mr-2">{countDoneProfiles}</span>
      </ManualProfileRowDataBadge>
    </div>
  );
}

const ManualScrape = ({
  isReadOnly,
  report,
  handleExit,
  isAmendment,
}: Props) => {
  const navigate = useNavigate();
  const { resetTimer } = useReportTimer();
  const { canPostScrape } = usePermissionValidator();
  const [profilesState, setProfiles] = useState<ProfilesState>(defaultState);
  const [showSystemProfiles, setShowSystemProfiles] = useState(false);
  const [errorSavingPosts, setErrorSavingPosts] = useState(false);
  const { isLoadingProfiles, isSuccessProfiles, profileList } =
    useReportDetail();
  const { mutate: getComputedBlueprint, data: computedBlueprint } =
    useGetComputedBlueprintFromWorkflow();
  const [activeScrapeProfileId, setActiveScrapeProfileId] = useState<
    string | null
  >(null);

  const {
    isLoading: isLoadingPostScrapeDone,
    isSuccess: isSuccessManualPostScrapeDone,
    mutate: postScrapeDone,
    error: errorPostScrapeDone,
  } = usePostScrapeDone();

  useEffect(() => {
    if (isSuccessProfiles) {
      resetTimer?.();
    }
  }, [isSuccessProfiles, resetTimer]);

  useEffect(() => {
    if (!profileList) return;
    setProfiles(buildProfilesState(profileList));
    if (report) {
      getComputedBlueprint({
        blueprintUUID: report.blueprint_uuid,
        companyUUID: report.company_uuid,
        behaviorKitUUID: report.behavior_kit_uuid || "None",
        workflowRunUUID: report.workflow_run_uuid,
      });
    }
  }, [profileList, report, getComputedBlueprint]);

  useEffect(() => {
    if (isSuccessManualPostScrapeDone) {
      if (isAmendment) {
        navigate("/reports/amendment", { state: { stepDone: true } });
      } else {
        navigate("/reports/next_task", { state: { stepDone: true } });
      }
    }
  }, [isSuccessManualPostScrapeDone, isAmendment, navigate]);

  function handleAction(action: MANUAL_PROFILE_ACTION, itemUuid: string) {
    let updatedProfileScrapeStatus: SCRAPE_STATUS;
    switch (action) {
      case MANUAL_PROFILE_ACTION.SKIP:
        updatedProfileScrapeStatus =
          activeScrapeProfileId === itemUuid
            ? SCRAPE_STATUS.DONE
            : SCRAPE_STATUS.SKIPPED;
        setErrorSavingPosts(false);
        if (activeScrapeProfileId === itemUuid) setActiveScrapeProfileId(null);
        break;
      case MANUAL_PROFILE_ACTION.START:
        updatedProfileScrapeStatus = SCRAPE_STATUS.IN_PROGRESS;
        setActiveScrapeProfileId(itemUuid);
        break;
      case MANUAL_PROFILE_ACTION.RESTART:
        updatedProfileScrapeStatus = SCRAPE_STATUS.IN_PROGRESS;
        setActiveScrapeProfileId(itemUuid);
        break;
      case MANUAL_PROFILE_ACTION.COMPLETE:
        updatedProfileScrapeStatus = SCRAPE_STATUS.DONE;
        setErrorSavingPosts(false);
        if (activeScrapeProfileId === itemUuid) setActiveScrapeProfileId(null);
        break;
      case MANUAL_PROFILE_ACTION.RESET:
        setErrorSavingPosts(true);
        if (activeScrapeProfileId === itemUuid) setActiveScrapeProfileId(null);
        break;
      default:
        break;
    }

    profileList?.map((profile) => {
      if (profile.item.external_id === itemUuid) {
        const updatedProfile = Object.assign({}, profile);
        updatedProfile.item.content = {
          ...profile.item.content,
          scrape_status: updatedProfileScrapeStatus,
        };
        return updatedProfile;
      }
      return profile;
    });

    setProfiles(buildProfilesState(profileList));
  }

  function isSaveDisabled() {
    // if there is at least one manual profile without status, still in progress, todo
    // then disable save button
    const oneProfilePending = profilesState.profilesToScrape.find((profile) => {
      const status = profile.item.content.scrape_status;
      return (
        !status ||
        status === SCRAPE_STATUS.IN_PROGRESS ||
        status === SCRAPE_STATUS.TO_DO
      );
    });

    return isReadOnly || !!oneProfilePending || !canPostScrape;
  }

  function handleSave() {
    if (!report) return;
    postScrapeDone({
      reportUUID: report.report_uuid,
      workflowRunUUID: report.workflow_run_uuid,
    });
  }

  if (isLoadingProfiles) return <LoadingSpinner />;

  const disableSave = isSaveDisabled();

  return (
    <div>
      <div className="flex items-center justify-end pb-3">
        <span
          className="leading-4 text-xs font-medium mr-4 text-orange-500 cursor-pointer"
          onClick={handleExit}
        >
          {isAmendment ? "Back to List" : "Exit Profile Confirm"}
        </span>
        {isAmendment && (
          <Button
            text="Complete Manual Scrape"
            className="px-13"
            disabled={disableSave}
            onClick={handleSave}
          />
        )}
      </div>
      <div className="m-2 p-4 border rounded-md border-gray-300 bg-gray-100">
        <div className="pb-4 text-2xl font-extrabold border-b border-gray-300">
          Profiles to Scrape
        </div>
        {report &&
          profilesState.profilesToScrape &&
          profilesState.profilesToScrape.map((item: ItemWithMetaData) => (
            <ManualProfileRow
              reportUUID={report.report_uuid}
              item={item}
              key={item.item.external_id}
              isReadOnly={isReadOnly || !canPostScrape}
              rowType={ManualProfileRowType.MANUAL}
              onAction={handleAction}
              blueprint={computedBlueprint}
              activeScrapeProfileId={activeScrapeProfileId}
            />
          ))}
        <div className="flex mt-4 justify-end">
          {!isAmendment && (
            <Button
              text="Complete Manual Scrape"
              className="px-16"
              disabled={disableSave}
              onClick={handleSave}
            />
          )}

          {(errorPostScrapeDone || errorSavingPosts) && (
            <ErrorMessageContainer message="Couldn't save, please contact an administrator" />
          )}
        </div>
      </div>

      <div
        className="m-2 p-4 border rounded-md border-gray-300 bg-gray-100 cursor-pointer"
        onClick={() => setShowSystemProfiles(!showSystemProfiles)}
      >
        <div
          className={classNames(
            "flex  text-2xl font-extrabold  justify-between items-center",
            showSystemProfiles ? "pb-4 border-b border-gray-300" : ""
          )}
        >
          <span>System Scraped Profiles</span>
          <div>
            <SkippedProfilesSummary profiles={profilesState.systemProfiles} />
          </div>
          <div className="w-6 h-6 text-gray-400">
            {showSystemProfiles ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </div>
        {showSystemProfiles && (
          <div>
            {report &&
              profilesState.systemProfiles &&
              profilesState.systemProfiles.map((item: ItemWithMetaData) => (
                <ManualProfileRow
                  reportUUID={report.report_uuid}
                  item={item}
                  key={item.item.external_id}
                  isReadOnly={true}
                  rowType={ManualProfileRowType.SYSTEM}
                  onAction={handleAction}
                  blueprint={computedBlueprint}
                  activeScrapeProfileId={activeScrapeProfileId}
                />
              ))}
          </div>
        )}
      </div>

      <div className="m-2 p-4 border rounded-md border-gray-300 bg-gray-100">
        <div className="pb-4 text-2xl font-extrabold border-b border-gray-300">
          Skipped Profiles
        </div>
        {report &&
          profilesState.skippedProfiles &&
          profilesState.skippedProfiles.map((item: ItemWithMetaData) => (
            <ManualProfileRow
              reportUUID={report.report_uuid}
              item={item}
              key={item.item.external_id}
              isReadOnly={true}
              rowType={ManualProfileRowType.SKIPPED}
              onAction={handleAction}
              blueprint={computedBlueprint}
              activeScrapeProfileId={activeScrapeProfileId}
            />
          ))}
      </div>
      {isLoadingPostScrapeDone && <LoadingSpinner />}
    </div>
  );
};

export default ManualScrape;
